// ProgressBar — the linear progress indicator, pure.
//
// Rebuilt from foundations v1 against the design vision (.claude/research/design/06).
//
// Determinate vs indeterminate is DERIVED from the datum, not a mode flag:
// `value` present → the bar fills to that percentage (role="progressbar" with
// aria-valuenow); `value` omitted → indeterminate, an animated segment slides
// and aria-valuenow is left off (the ARIA convention for "amount unknown").
// This is genuine data-optionality, not a boolean that swaps meaning — the
// same shape HTML's own <progress> uses — so no discriminant flag is spent.
//
// Tone is a discriminated `ProgressBarTone` union, defaulting to `neutral`.
// The accent is precious (§1: reserved for the single primary action or the
// agent's identity), so a bar only spends it when it literally represents
// that primary/agent work (e.g. the agent generating material). Everything
// else fills neutral. Colour is never the sole channel (SC 1.4.1): progress
// is carried by the fill's WIDTH; tone only says *whose* work it is.
//
// Pure/props-driven: `value` + `tone` pick the look; `label` is the accessible
// name for the role="progressbar" region. `label` is a caller-resolved Lingui
// string; when omitted we fall back to a localized "Progress" via the `t`
// macro (deps arrive through the Lingui context, no I/O here).

import { useLingui } from "@lingui/react/macro";
import "./ProgressBar.css";

type ProgressBarTone = "accent" | "neutral";

export type ProgressBarProps = {
    // 0–100. Omit for an indeterminate bar (amount unknown). Out-of-range
    // values are clamped to the track — a measurement, never a design value.
    readonly value?: number;
    // Who the progress belongs to. `accent` only when the bar IS the view's
    // primary action or the agent's work; otherwise `neutral` (the default).
    readonly tone?: ProgressBarTone;
    // Accessible name for the progressbar region. Caller passes a Lingui-
    // resolved string; omitted → a localized "Progress" default.
    readonly label?: string;
    // Optional layout hook (width/margins in the composing context); never
    // used to restyle the track or fill themselves.
    readonly className?: string;
};

export const ProgressBar = ({
    value,
    tone = "neutral",
    label,
    className,
}: ProgressBarProps) => {
    const { t } = useLingui();
    const clamped =
        value === undefined ? undefined : Math.min(100, Math.max(0, value));
    const indeterminate = clamped === undefined;

    return (
        <div
            role="progressbar"
            aria-label={label ?? t`Progress`}
            aria-valuemin={0}
            aria-valuemax={100}
            // Omitted (undefined) for the indeterminate bar — React drops the
            // attribute, which is exactly how AT reads "amount unknown".
            aria-valuenow={clamped}
            className={`bilda-progress bilda-progress--${tone}${
                indeterminate ? " bilda-progress--indeterminate" : ""
            }${className ? ` ${className}` : ""}`}
        >
            <span
                className="bilda-progress__fill"
                // Determinate width is the datum itself; the indeterminate
                // fill takes its width + motion from CSS.
                style={indeterminate ? undefined : { width: `${clamped}%` }}
            />
        </div>
    );
};
